import { Logo } from "../components/Logo";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Users, FileText, CreditCard, Bell, Settings, MessageSquare, LayoutDashboard, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { WorkStatusPanel, WorkStatusBadge, PaymentBadge } from "../components/WorkStatusPanel";
import { TemplatesPanel } from "../components/TemplatesPanel";
import { Database, FolderOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };
const TABS = [
  { key: "overview", label: "Panoramica", icon: LayoutDashboard },
  { key: "users", label: "Utenti", icon: Users },
  { key: "requests", label: "Richieste", icon: FileText },
  { key: "payments", label: "Pagamenti", icon: CreditCard },
  { key: "notifications", label: "Notifiche", icon: Bell },
  { key: "messages", label: "Messaggi", icon: MessageSquare },
  { key: "portfolio", label: "Progetti realizzati", icon: FolderOpen },
  { key: "database", label: "Database e documenti", icon: Database },
  { key: "settings", label: "Impostazioni", icon: Settings },
];
const fmtDate = (d) => (d ? new Date(d).toLocaleString("it-IT") : "—");
const fmtEur = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-ink/10 rounded-sm p-6">
    <p className="text-xs uppercase tracking-[0.2em] text-inksoft">{label}</p>
    <p className="mt-3 font-display text-4xl font-semibold text-ink">{value}</p>
  </div>
);

const Overview = ({ stats }) => {
  const seg = stats.segmentation || {};
  const labels = stats.segmentation_labels || {};
  const total = Object.values(seg).reduce((a, b) => a + b, 0) || 1;
  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-users" label="Utenti" value={stats.users} />
        <Stat testid="stat-requests" label="Richieste concept" value={`${stats.requests_ready}/${stats.requests}`} />
        <Stat testid="stat-payments" label="Pagamenti confermati" value={stats.payments} />
        <Stat testid="stat-revenue" label="Incassato" value={fmtEur(stats.revenue)} />
        <Stat testid="stat-b2c" label="Clienti B2C (privati)" value={stats.b2c_users ?? 0} />
        <Stat testid="stat-b2b" label="Clienti B2B (professionisti, studi, rivenditori, imprese)" value={stats.b2b_users ?? 0} />
        <Stat testid="stat-pa" label="Pubblica Amministrazione" value={stats.pa_users ?? 0} />
        <Stat testid="stat-invoices" label="Richieste fattura elettronica" value={stats.invoices_requested ?? 0} />
        <Stat testid="stat-notifications" label="Notifiche da leggere" value={stats.unread_notifications} />
        <Stat testid="stat-messages" label="Messaggi nuovi" value={stats.messages} />
      </div>
      <div data-testid="admin-segmentation" className="bg-white border border-ink/10 rounded-sm p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-inksoft">Segmentazione clienti — target commerciale</p>
        <div className="mt-5 space-y-3">
          {Object.entries(seg).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <div key={k} data-testid={`segment-${k}`} className="grid grid-cols-12 items-center gap-3 text-sm">
              <span className="col-span-12 sm:col-span-5 text-ink">{labels[k] || k}</span>
              <div className="col-span-9 sm:col-span-5 h-2 bg-sand rounded-full overflow-hidden"><div className="h-full bg-amberdark" style={{ width: `${Math.round((n / total) * 100)}%` }} /></div>
              <span className="col-span-3 sm:col-span-2 text-right text-inksoft">{n} utenti · {stats.paid_requests_by_type?.[k] || 0} pagati</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TYPE_SHORT = { privato: "Privato", professionista: "Professionista", studio: "Studio", distributore: "Distributore", impresa: "Impresa", pa: "PA", altro: "Altro" };

const UsersTab = ({ users, reload }) => {
  const update = async (u, patch) => {
    try {
      await axios.patch(`${API}/admin/users/${u.user_id}`, patch, cfg);
      toast.success("Utente aggiornato");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  return (
    <div data-testid="admin-users" className="overflow-x-auto bg-white border border-ink/10 rounded-sm">
      <table className="w-full text-sm">
        <thead className="bg-sand text-xs uppercase tracking-[0.15em] text-inksoft">
          <tr>{["Nome", "Email", "Tipologia", "Azienda / Fiscale", "Provider", "Piano", "Crediti", "Admin"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-t border-ink/10">
              <td className="px-4 py-3 text-ink">{u.name}{u.profile?.phone && <br />}<span className="text-xs text-inksoft">{u.profile?.phone}</span></td>
              <td className="px-4 py-3 text-inksoft">{u.email}</td>
              <td className="px-4 py-3" data-testid={`admin-user-type-${u.user_id}`}>
                <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold rounded-sm px-2 py-1 ${u.profile?.segment === "B2B" ? "bg-sage/20 text-sagedark" : u.profile?.segment === "PA" ? "bg-amberl/20 text-amberdark" : "bg-sand text-inksoft"}`}>
                  {u.profile?.user_type ? `${TYPE_SHORT[u.profile.user_type] || u.profile.user_type} · ${u.profile.segment}` : "—"}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-inksoft">
                {u.profile?.company_name && <span className="text-ink">{u.profile.company_name}</span>}{u.profile?.company_city && ` · ${u.profile.company_city}`}
                {u.profile?.billing?.vat && <><br />P.IVA {u.profile.billing.vat}</>}{u.profile?.billing?.fiscal_code && <><br />CF {u.profile.billing.fiscal_code}</>}
                {u.profile?.billing?.sdi && ` · SDI ${u.profile.billing.sdi}`}{u.profile?.billing?.cuu && ` · CUU ${u.profile.billing.cuu}`}
                {u.profile?.invoice_requested && <><br /><span className="text-amberdark">Fattura richiesta</span></>}
              </td>
              <td className="px-4 py-3 text-inksoft">{u.auth_provider}</td>
              <td className="px-4 py-3">
                <select data-testid={`admin-user-plan-${u.user_id}`} value={u.plan || "free"} onChange={(e) => update(u, { plan: e.target.value })} className="border border-ink/15 rounded-sm h-9 px-2 bg-white text-ink">
                  {["free", "pay_per_use", "studio"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </td>
              <td className="px-4 py-3">
                <input
                  data-testid={`admin-user-credits-${u.user_id}`}
                  type="number" min="0" defaultValue={u.credits ?? 0}
                  onBlur={(e) => Number(e.target.value) !== (u.credits ?? 0) && update(u, { credits: Number(e.target.value) })}
                  className="w-20 border border-ink/15 rounded-sm h-9 px-2 bg-white text-ink"
                />
              </td>
              <td className="px-4 py-3">
                <button
                  data-testid={`admin-user-toggle-admin-${u.user_id}`}
                  onClick={() => update(u, { is_admin: !u.is_admin })}
                  className={`text-xs font-semibold uppercase tracking-[0.15em] rounded-sm px-3 py-1.5 ${u.is_admin ? "bg-ink text-cream" : "border border-ink/20 text-inksoft"}`}
                >
                  {u.is_admin ? "Admin" : "Utente"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RequestsTab = ({ requests, onUpdate }) => (
  <div data-testid="admin-requests" className="space-y-4">
    {requests.map((r) => (
      <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-white border border-ink/10 rounded-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-display text-xl text-ink capitalize">{r.space_type} — {r.surface} mq · {r.name}</p>
            <p className="text-xs text-inksoft mt-1">{r.email} · {fmtDate(r.created_at)} · controllo: {r.control_system || "—"} · piano: {r.plan || "—"} · stato: {r.status || "—"}</p>
            {r.plan === "intermedio" && (
              <form data-testid={`admin-video-form-${r.id}`} onSubmit={async (e) => { e.preventDefault(); const url = e.target.elements.video_url.value; try { await axios.patch(`${API}/admin/requests/${r.id}/video`, { video_url: url }, { withCredentials: true }); toast.success("Link video salvato"); } catch { toast.error("Errore salvataggio video"); } }} className="mt-2 flex gap-2 items-center">
                <input name="video_url" data-testid={`admin-video-input-${r.id}`} defaultValue={r.video_url || ""} placeholder="Link video 3D (YouTube/Vimeo) — il QR del report punta qui" className="flex-1 h-8 px-2 text-xs bg-cream border border-ink/15 rounded-sm" />
                <button type="submit" data-testid={`admin-video-save-${r.id}`} className="h-8 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] bg-ink text-cream rounded-sm">Salva video</button>
              </form>
            )}
            {r.plan === "advanced" && (
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <button type="button" data-testid={`admin-briefing-send-${r.id}`} onClick={async () => { if (!window.confirm(`Reinviare a ${r.email} l'email con il link del Briefing Esecutivo?`)) return; try { const { data } = await axios.post(`${API}/admin/requests/${r.id}/quote-accepted`, {}, { withCredentials: true }); onUpdate({ id: r.id, briefing_sent_at: data.briefing_sent_at }); toast.success("Email con il link del Briefing Esecutivo inviata al cliente"); } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } }} className="h-8 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] bg-ink text-cream rounded-sm">
                  Reinvia link Briefing
                </button>
                {r.briefing_sent_at && <span data-testid={`admin-briefing-sent-${r.id}`} className="text-xs text-inksoft">Link Briefing inviato il {fmtDate(r.briefing_sent_at)}</span>}
              </div>
            )}
            {r.customer_profile && <p data-testid={`admin-request-profile-${r.id}`} className="text-xs text-inksoft mt-1">{TYPE_SHORT[r.customer_profile.user_type] || r.customer_profile.user_type} ({r.customer_profile.segment}){r.customer_profile.company_name && ` · ${r.customer_profile.company_name}`}{r.customer_profile.billing?.vat && ` · P.IVA ${r.customer_profile.billing.vat}`}{r.customer_profile.billing?.fiscal_code && ` · CF ${r.customer_profile.billing.fiscal_code}`}</p>}
          </div>
          <div className="flex items-center gap-2">
            <PaymentBadge payment={r.payment} testid={`admin-payment-badge-${r.id}`} />
            <WorkStatusBadge status={r.work_status} testid={`admin-work-badge-${r.id}`} />
            <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold rounded-sm px-3 py-1.5 ${r.ai_status === "ready" ? "bg-sage/20 text-sagedark" : "bg-sand text-inksoft"}`}>{r.ai_status}</span>
          </div>
        </div>
        {r.photometric && (
          <p className="mt-3 text-xs text-inksoft">
            Em {r.photometric.em_lumen_method} lx · U0 {r.photometric.u0} · UGR {r.photometric.ugr} · LENI {r.photometric.leni_kwh_m2_year} kWh/mq·anno · {r.photometric.luminaire.count} corpi
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.15em]">
          {r.pdf_path && <a data-testid={`admin-request-pdf-${r.id}`} href={`${API}/files/${r.pdf_path}`} target="_blank" rel="noreferrer" className="border border-ink/20 hover:border-ink rounded-sm px-3 py-2 text-ink">PDF</a>}
          {r.bom_path && <a href={`${API}/files/${r.bom_path}`} target="_blank" rel="noreferrer" className="border border-ink/20 hover:border-ink rounded-sm px-3 py-2 text-ink">BOM Excel</a>}
          {r.editable_path && <a href={`${API}/files/${r.editable_path}`} target="_blank" rel="noreferrer" className="border border-ink/20 hover:border-ink rounded-sm px-3 py-2 text-ink">Editabile</a>}
          {(r.attachments || []).map((a, i) => <a key={i} href={`${API}/files/${a.storage_path}`} target="_blank" rel="noreferrer" className="border border-amberdark/40 hover:border-amberdark rounded-sm px-3 py-2 text-amberdark">{a.original_name}</a>)}
        </div>
        <WorkStatusPanel request={r} onSaved={onUpdate} />
      </div>
    ))}
    {requests.length === 0 && <p className="text-inksoft">Nessuna richiesta.</p>}
  </div>
);

const PaymentsTab = ({ payments }) => (
  <div data-testid="admin-payments" className="overflow-x-auto bg-white border border-ink/10 rounded-sm">
    <table className="w-full text-sm">
      <thead className="bg-sand text-xs uppercase tracking-[0.15em] text-inksoft">
        <tr>{["Data", "Cliente", "Servizio", "Importo", "Stato"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {payments.map((p) => (
          <tr key={p.session_id} data-testid={`admin-payment-${p.session_id}`} className="border-t border-ink/10">
            <td className="px-4 py-3 text-inksoft">{fmtDate(p.created_at)}</td>
            <td className="px-4 py-3 text-ink">{p.customer_name || "Ospite"}<br /><span className="text-xs text-inksoft">{p.customer_email || ""}</span></td>
            <td className="px-4 py-3 text-inksoft">{p.lookup_key}</td>
            <td className="px-4 py-3 text-ink">{fmtEur(p.amount)}</td>
            <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.2em] font-semibold rounded-sm px-2.5 py-1 ${["paid","authorized"].includes(p.payment_status) ? "bg-sage/20 text-sagedark" : "bg-sand text-inksoft"}`}>{p.payment_status}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
    {payments.length === 0 && <p className="p-6 text-inksoft">Nessun pagamento.</p>}
  </div>
);

const NotificationsTab = ({ notifications, markRead }) => (
  <div data-testid="admin-notifications" className="space-y-3">
    <div className="flex justify-end">
      <button data-testid="admin-notifications-read" onClick={markRead} className="text-xs font-semibold uppercase tracking-[0.15em] border border-ink/20 hover:border-ink rounded-sm px-4 py-2 text-ink">Segna tutte come lette</button>
    </div>
    {notifications.map((n) => (
      <div key={n.id} data-testid={`admin-notification-${n.id}`} className={`bg-white border rounded-sm p-5 ${n.read ? "border-ink/10" : "border-amberdark/60 bg-amberl/5"}`}>
        <div className="flex flex-wrap justify-between gap-2">
          <p className="font-semibold text-ink">Pagamento Stripe confermato — {n.service}</p>
          <p className="text-xs text-inksoft">{fmtDate(n.created_at)}</p>
        </div>
        <p className="mt-2 text-sm text-inksoft">Cliente: <span className="text-ink">{n.customer_name}</span> {n.customer_email && `(${n.customer_email})`} · Importo: <span className="text-ink">{fmtEur(n.amount)}</span> · Destinatario: {n.recipient}</p>
        {n.report_link ? <a href={`${process.env.REACT_APP_BACKEND_URL}${n.report_link}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold uppercase tracking-[0.15em] text-amberdark hover:underline">Apri report generato</a> : <p className="mt-2 text-xs text-inksoft/70">Nessun report ancora generato per questo cliente</p>}
      </div>
    ))}
    {notifications.length === 0 && <p className="text-inksoft">Nessuna notifica.</p>}
  </div>
);

const MessagesTab = ({ messages }) => (
  <div data-testid="admin-messages" className="space-y-3">
    {messages.map((m) => (
      <div key={m.id} className="bg-white border border-ink/10 rounded-sm p-5">
        <div className="flex flex-wrap justify-between gap-2">
          <p className="font-semibold text-ink">{m.name} · <a href={`mailto:${m.email}`} className="text-amberdark">{m.email}</a></p>
          <p className="text-xs text-inksoft">{m.topic} · {fmtDate(m.created_at)}</p>
        </div>
        <p className="mt-2 text-sm text-inksoft whitespace-pre-line">{m.message}</p>
      </div>
    ))}
    {messages.length === 0 && <p className="text-inksoft">Nessun messaggio.</p>}
  </div>
);

const SettingsTab = ({ settings, reload }) => {
  const [form, setForm] = useState({ calendly_url: settings.calendly_url || "", briefing_url: settings.briefing_url || "", price_list: settings.price_list || "", notify_email: settings.notify_email || "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/settings`, form, cfg);
      toast.success("Impostazioni salvate");
      reload();
    } catch (err) {
      toast.error("Salvataggio non riuscito");
    }
  };
  return (
    <>
    <form data-testid="admin-settings" onSubmit={save} className="bg-white border border-ink/10 rounded-sm p-8 max-w-xl space-y-6">
      <div className="space-y-2">
        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-inksoft">Link Google Calendar — Call conoscitiva 15 min (pulsante "Prenota una call conoscitiva")</label>
        <input data-testid="admin-settings-calendly" value={form.calendly_url} onChange={(e) => setForm({ ...form, calendly_url: e.target.value })} className="w-full h-11 px-3 border border-ink/15 rounded-sm bg-white text-ink" />
      </div>
      <div className="space-y-2">
        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-inksoft">Link Google Calendar — Videocall di Briefing Esecutivo</label>
        <input data-testid="admin-settings-briefing" value={form.briefing_url} onChange={(e) => setForm({ ...form, briefing_url: e.target.value })} className="w-full h-11 px-3 border border-ink/15 rounded-sm bg-white text-ink" />
        <p className="text-xs text-inksoft/70">Non compare sul sito: viene inviato al cliente nell'email di conferma della richiesta di Consulenza Esecutiva (in Richieste puoi reinviarlo con "Reinvia link Briefing").</p>
      </div>
      <div className="space-y-2">
        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-inksoft">Listino prezzi indicativo (email di conferma)</label>
        <textarea data-testid="admin-settings-price-list" rows={6} value={form.price_list} onChange={(e) => setForm({ ...form, price_list: e.target.value })} className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-ink" />
        <p className="text-xs text-inksoft/70">Lascia vuoto per non mostrarlo nell'email.</p>
      </div>
      <div className="space-y-2">
        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-inksoft">Email notifiche pagamenti</label>
        <input data-testid="admin-settings-notify-email" type="email" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} className="w-full h-11 px-3 border border-ink/15 rounded-sm bg-white text-ink" />
        <p className="text-xs text-inksoft/70">Le notifiche vengono registrate nel pannello. L'invio email/Telegram esterno sarà attivato in seguito.</p>
      </div>
      <button data-testid="admin-settings-save" type="submit" className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-espresso rounded-sm px-6 h-11 text-xs font-semibold uppercase tracking-[0.15em]">
        <Save className="w-4 h-4" strokeWidth={1.5} /> Salva
      </button>
    </form>
    </>
  );
};

const isProductUrl = (u) => /^https?:\/\/[^/]+\/.+/.test(u || "");

const DemoModeToggle = () => {
  const [state, setState] = useState({ enabled: false, loaded: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/settings/demo-mode`, cfg).then((r) => setState({ ...r.data, loaded: true })).catch(() => setState((s) => ({ ...s, loaded: true }))); }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/settings/demo-mode`, { enabled: !state.enabled }, cfg);
      setState({ ...r.data, loaded: true });
      toast.success(r.data.enabled ? "Modalità demo attivata: report di esempio, nessun servizio AI, nessun pagamento" : "Modalità demo disattivata: generazione reale per i clienti");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento modalità demo"); }
    finally { setBusy(false); }
  };
  if (!state.loaded) return null;
  return (
    <div data-testid="admin-demo-mode" className={`mt-6 flex flex-wrap items-center justify-between gap-4 rounded-sm border px-5 py-4 ${state.enabled ? "border-[#C5A059] bg-[#C5A059]/15" : "border-ink/10 bg-white"}`}>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#8A6A2B]">Modalità test / demo {state.enabled ? "· ATTIVA" : "· disattivata"}</p>
        <p data-testid="admin-demo-mode-desc" className="mt-1 text-sm text-ink">
          {state.enabled ? "Le nuove richieste Lighting Concept ricevono un report di esempio predefinito e l'email di consegna, senza pagamento e senza alcuna chiamata ai servizi AI o di generazione immagini (nessun credito consumato)." : "Le richieste dei clienti seguono il flusso reale: pagamento con carta e generazione del report personalizzato."}
        </p>
        {state.updated_at && <p className="mt-1 text-[11px] text-inksoft">Ultima modifica {new Date(state.updated_at).toLocaleString("it-IT")}{state.updated_by ? ` · ${state.updated_by}` : ""}</p>}
      </div>
      <button type="button" role="switch" aria-checked={state.enabled} data-testid="admin-demo-mode-toggle" onClick={toggle} disabled={busy}
        className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${state.enabled ? "bg-[#C5A059]" : "bg-ink/20"}`}>
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${state.enabled ? "translate-x-8" : "translate-x-1"}`} />
      </button>
    </div>
  );
};

const ProductLinkCell = ({ product, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(product.url || "");
  const save = async () => {
    try { const r = await axios.patch(`${API}/admin/products/${product.id}/url`, { url: val }, cfg); toast.success(val ? "Link prodotto salvato" : "Link rimosso"); setEdit(false); onSaved?.(r.data.url); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio link"); }
  };
  if (edit) {
    return (
      <div className="flex items-center gap-1 min-w-[260px]">
        <input data-testid={`admin-kb-url-input-${product.id}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="https://brand.com/.../prodotto" className="flex-1 h-8 px-2 border border-ink/20 rounded-sm text-xs" />
        <button type="button" data-testid={`admin-kb-url-save-${product.id}`} onClick={save} className="h-8 px-2 bg-ink text-cream rounded-sm text-[10px] uppercase font-semibold">Salva</button>
        <button type="button" onClick={() => { setEdit(false); setVal(product.url || ""); }} className="h-8 px-2 border border-ink/20 rounded-sm text-[10px] uppercase">Annulla</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {isProductUrl(product.url) ? <a data-testid={`admin-kb-link-${product.id}`} href={product.url} target="_blank" rel="noreferrer" className="text-sagedark underline">pagina prodotto{product.url_source === "auto" ? " · auto" : ""}</a> : <span data-testid={`admin-kb-nolink-${product.id}`} className="text-red-700">mancante</span>}
      <button type="button" data-testid={`admin-kb-url-edit-${product.id}`} onClick={() => setEdit(true)} className="text-[10px] uppercase tracking-wide text-inksoft hover:text-ink underline">modifica</button>
    </div>
  );
};

const KnowledgeBasePanel = () => {
  const [kb, setKb] = useState({ count: 0, items: [], filename: null, uploaded_at: null, issues: [] });
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState({});
  const load = () => axios.get(`${API}/admin/products`, cfg).then((r) => setKb(r.data)).catch(() => {});
  const loadLinks = () => axios.get(`${API}/admin/products/links-status`, cfg).then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); loadLinks(); }, []);
  useEffect(() => {
    if (!links.running) return undefined;
    const t = setInterval(() => { loadLinks(); }, 4000);
    return () => clearInterval(t);
  }, [links.running]);
  useEffect(() => { if (links.running === false) load(); }, [links.running]);
  const resolveLinks = async () => {
    try { await axios.post(`${API}/admin/products/resolve-links`, {}, cfg); toast.success("Ricerca link prodotto avviata"); loadLinks(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore avvio ricerca"); }
  };
  const upload = async (e) => {
    e.preventDefault();
    const file = e.target.elements.file.files[0];
    if (!file) return toast.error("Seleziona il file Excel (.xlsx)");
    const data = new FormData();
    data.append("file", file);
    setBusy(true);
    try { const r = await axios.post(`${API}/admin/products/upload`, data, cfg); toast.success(`Knowledge Base aggiornata: ${r.data.count} prodotti`); e.target.reset(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-kb" className="mt-8 bg-white border border-ink/10 rounded-sm p-8 max-w-3xl space-y-5">
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amberdark">Knowledge Base prodotti — unica fonte per i report</p>
      <p className="text-xs text-inksoft">Carica l'Excel KNOWLEDGE_BASE_PRODOTTI (colonne: BRAND · IMMAGINE PRODOTTO · IMMAGINE AMBIENTATA · NOME PRODOTTO · Categoria/Tipo · DESCRIZIONE EFFETTO · IMMAGINE EFFETTO). Ogni caricamento sostituisce l'elenco precedente.</p>
      <form onSubmit={upload} className="flex flex-wrap items-center gap-3">
        <input name="file" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-testid="admin-kb-file" className="text-sm" />
        <button type="submit" disabled={busy} data-testid="admin-kb-upload" className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-espresso disabled:opacity-60 rounded-sm px-6 h-11 text-xs font-semibold uppercase tracking-[0.15em]">{busy ? "Caricamento…" : "Carica Knowledge Base"}</button>
      </form>
      <div className="flex flex-wrap gap-3">
        <a data-testid="admin-kb-template" href={`${API}/admin/products/template`} className="inline-flex items-center border border-ink/20 hover:border-ink rounded-sm px-4 h-10 text-xs font-semibold uppercase tracking-[0.12em] text-ink">Scarica modello Excel</a>
        {kb.count > 0 && (
          <button type="button" data-testid="admin-kb-resolve-links" onClick={resolveLinks} disabled={!!links.running} className="inline-flex items-center gap-2 border border-[#C5A059] text-[#8A6A2B] hover:bg-[#C5A059] hover:text-ink disabled:opacity-60 rounded-sm px-4 h-10 text-xs font-semibold uppercase tracking-[0.12em]">
            {links.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}{links.running ? `Ricerca link ${links.done || 0}/${links.total || 0}` : "Trova link pagine prodotto"}
          </button>
        )}
        {kb.count > 0 && [...new Set(kb.items.map((p) => p.brand))].map((b) => (
          <a key={b} data-testid={`admin-kb-sheet-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} href={`${API}/admin/products/sheet?brand=${encodeURIComponent(b)}`} target="_blank" rel="noreferrer" className="inline-flex items-center bg-[#C5A059] hover:bg-ink hover:text-cream rounded-sm px-4 h-10 text-xs font-semibold uppercase tracking-[0.12em] text-ink">Scheda prodotti PDF · {b}</a>
        ))}
      </div>
      {kb.issues?.some((i) => i.errors.length) && (
        <ul data-testid="admin-kb-issues" className="text-xs space-y-1 border border-red-300 bg-red-50 rounded-sm p-3">
          {kb.issues.filter((i) => i.errors.length).map((i) => <li key={i.id} className="text-red-700"><strong>{i.brand} — {i.name}</strong>: {i.errors.join("; ")}</li>)}
        </ul>
      )}
      {links.total_products > 0 && <p data-testid="admin-kb-links-count" className="text-xs text-inksoft"><strong className="text-ink">{links.with_link}</strong> prodotti su {links.total_products} con link alla pagina prodotto ufficiale (pulsante "Apri il catalogo ufficiale" nei report){links.running === false && links.total > 0 ? ` · ultima ricerca: ${links.found} trovati su ${links.total}` : ""}</p>}
      <p data-testid="admin-kb-count" className="text-sm text-ink"><strong>{kb.count}</strong> prodotti attivi{kb.filename ? ` · ${kb.filename}` : ""}{kb.uploaded_at ? ` · ${new Date(kb.uploaded_at).toLocaleString("it-IT")}` : ""}</p>
      {kb.items.length > 0 && (
        <div className="overflow-x-auto">
          <table data-testid="admin-kb-table" className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.12em] text-inksoft border-b border-ink/10"><th className="py-2 pr-3">Brand</th><th className="py-2 pr-3">Prodotto</th><th className="py-2 pr-3">Spazi</th><th className="py-2 pr-3">Effetto</th><th className="py-2 pr-3">Immagini</th><th className="py-2 pr-3">Link</th></tr></thead>
            <tbody>
              {kb.items.map((p) => (
                <tr key={p.id} data-testid={`admin-kb-row-${p.id}`} className="border-b border-ink/5 align-top">
                  <td className="py-2 pr-3 font-semibold text-ink">{p.brand}</td>
                  <td className="py-2 pr-3 text-ink">{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="underline">{p.name}</a> : p.name}{p.ip68 && <span className="ml-2 text-[10px] text-amberdark font-semibold">IP68</span>}<div className="text-inksoft">{p.type}</div></td>
                  <td className="py-2 pr-3 text-inksoft">{(p.spaces || []).join(", ")}</td>
                  <td className="py-2 pr-3 text-inksoft">{p.effect_label || p.effect}<div className="text-[11px]">{p.light_text}</div></td>
                  <td className="py-2 pr-3 text-inksoft">{p.img_product ? "prodotto " : ""}{p.img_ambient ? "ambientata " : ""}{p.img_effect ? "effetto" : ""}</td>
                  <td className="py-2 pr-3"><ProductLinkCell product={p} onSaved={(url) => { setKb((k) => ({ ...k, items: k.items.map((x) => (x.id === p.id ? { ...x, url, url_source: url ? "manual" : "" } : x)) })); loadLinks(); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// One project of "I nostri progetti": everything shown on the public page is edited here and saved in one go
const PortfolioItemEditor = ({ it, onSaved, onRemove }) => {
  const [f, setF] = useState(() => ({ title: it.title, kind: it.kind, wip: it.wip, facts: { type: "", year: "", city: "", area: "", ...it.facts }, banner_mode: it.banner_mode,
    banner_photo: it.banner_photo, cover_page: it.cover_page, plan_page: it.plan_page || "", order: it.order || 0 }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setFact = (k) => (e) => setF((x) => ({ ...x, facts: { ...x.facts, [k]: e.target.value } }));
  const save = async () => {
    if (f.banner_mode === "plan" && !parseInt(f.plan_page, 10)) return toast.error("Indica la pagina del PDF con la pianta");
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/portfolio/${it.id}`, {
        title: f.title, kind: f.kind, wip: f.wip, facts: f.facts, banner_mode: f.banner_mode, order: parseInt(f.order, 10) || 0,
        cover_page: parseInt(f.cover_page, 10) || 1, plan_page: parseInt(f.plan_page, 10) || 0, ...(it.photos ? { banner_photo: parseInt(f.banner_photo, 10) || 1 } : {}),
      }, { withCredentials: true });
      toast.success("Progetto aggiornato");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const inp = "h-9 px-2 border border-ink/15 rounded-sm bg-white text-ink text-sm";
  const lab = "flex flex-col gap-1 text-[11px] uppercase tracking-[0.12em] text-inksoft";
  return (
    <li data-testid={`admin-portfolio-item-${it.id}`} className="bg-cream/60 border border-ink/10 rounded-sm p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <input value={f.title} onChange={set("title")} data-testid={`admin-portfolio-name-${it.id}`} className={`${inp} flex-1`} />
        <span className="text-inksoft text-xs shrink-0">{it.pages} pag. · {it.photos || 0} foto</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className={lab}>Sezione
          <select value={f.kind} onChange={set("kind")} data-testid={`admin-portfolio-kind-${it.id}`} className={inp}>
            <option value="realized">Progetto realizzato</option>
            <option value="example">Esempio di elaborato (book)</option>
          </select>
        </label>
        <label className={lab}>Ordine (0 = primo)
          <input type="number" value={f.order} onChange={set("order")} className={inp} />
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs text-ink">
        <input type="checkbox" checked={f.wip} onChange={set("wip")} data-testid={`admin-portfolio-wip-${it.id}`} className="w-4 h-4 accent-[#C5A059]" />
        Work in progress (progetto in fase di sviluppo)
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <label className={lab}>Tipologia<input value={f.facts.type} onChange={setFact("type")} placeholder="Residenziale" className={inp} /></label>
        <label className={lab}>Anno<input value={f.facts.year} onChange={setFact("year")} placeholder="2025" className={inp} /></label>
        <label className={lab}>Città<input value={f.facts.city} onChange={setFact("city")} placeholder="Palermo" className={inp} /></label>
        <label className={lab}>Superficie<input value={f.facts.area} onChange={setFact("area")} placeholder="120 m²" className={inp} /></label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
        <label className={lab}>Banner
          <select value={f.banner_mode} onChange={set("banner_mode")} data-testid={`admin-portfolio-banner-${it.id}`} className={inp}>
            <option value="photo">Foto dal book</option>
            <option value="page">Pagina del book</option>
            <option value="plan">Nero con pianta bianca</option>
          </select>
        </label>
        <label className={lab}>Foto n.<input type="number" min={1} max={Math.max(1, it.photos || 0)} disabled={!it.photos} value={f.banner_photo} onChange={set("banner_photo")} className={inp} /></label>
        <label className={lab}>Pagina n.<input type="number" min={1} max={it.pages} value={f.cover_page} onChange={set("cover_page")} data-testid={`admin-portfolio-cover-${it.id}`} className={inp} /></label>
        <label className={lab}>Pag. pianta<input type="number" min={1} max={it.pages} value={f.plan_page} onChange={set("plan_page")} placeholder="—" data-testid={`admin-portfolio-plan-${it.id}`} className={inp} /></label>
      </div>
      <p className="text-[11px] text-inksoft">Foto n. = foto estratta dal PDF in risoluzione originale (usata anche come anteprima). Pagina n. = pagina intera del book. Pag. pianta = pagina con la planimetria, mostrata in bianco su nero.</p>
      <div className="flex items-center justify-between">
        <a href={`/progetti/${it.id}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.12em] text-amberdark hover:text-ink">Vedi pagina</a>
        <div className="flex items-center gap-4">
          <button type="button" data-testid={`admin-portfolio-delete-${it.id}`} onClick={() => onRemove(it.id)} className="text-xs uppercase tracking-[0.12em] text-inksoft hover:text-red-700">Elimina</button>
          <button type="button" disabled={saving} onClick={save} data-testid={`admin-portfolio-save-${it.id}`} className="bg-ink text-cream hover:bg-espresso disabled:opacity-60 rounded-sm px-4 h-9 text-xs font-semibold uppercase tracking-[0.15em]">{saving ? "Salvo…" : "Salva"}</button>
        </div>
      </div>
    </li>
  );
};

const PortfolioPanel = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/portfolio`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    e.preventDefault();
    const f = e.target.elements;
    if (!f.file.files[0]) return toast.error("Seleziona un file (PDF o immagine)");
    const data = new FormData();
    data.append("title", f.title.value);
    data.append("file", f.file.files[0]);
    setBusy(true);
    try { await axios.post(`${API}/admin/portfolio`, data, { withCredentials: true }); toast.success("Progetto pubblicato"); e.target.reset(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore upload"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo progetto dalla pagina?")) return;
    await axios.delete(`${API}/admin/portfolio/${id}`, { withCredentials: true }); load();
  };
  return (
    <div data-testid="admin-portfolio" className="bg-white border border-ink/10 rounded-sm p-8 max-w-3xl space-y-5">
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amberdark">I nostri progetti — progetti realizzati ed esempi di elaborati</p>
      <form onSubmit={upload} className="space-y-3">
        <input name="title" required data-testid="admin-portfolio-title" placeholder="Titolo (es. Residenza Milano)" className="w-full h-11 px-3 border border-ink/15 rounded-sm bg-white text-ink text-sm" />
        <input name="file" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" data-testid="admin-portfolio-file" className="w-full text-sm" />
        <button type="submit" disabled={busy} data-testid="admin-portfolio-upload" className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-espresso disabled:opacity-60 rounded-sm px-6 h-11 text-xs font-semibold uppercase tracking-[0.15em]">{busy ? "Caricamento…" : "Pubblica progetto"}</button>
      </form>
      <ul className="space-y-4">
        {items.map((it) => <PortfolioItemEditor key={`${it.id}-${it.title}-${it.kind}-${it.wip}-${it.banner_mode}-${it.plan_v}-${JSON.stringify(it.facts)}`} it={it} onSaved={load} onRemove={remove} />)}
      </ul>
    </div>
  );
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({ stats: null, users: [], requests: [], payments: [], notifications: [], messages: [], settings: null });

  const load = async () => {
    const [stats, users, requests, payments, notifications, messages, settings] = await Promise.all(
      ["stats", "users", "requests", "payments", "notifications", "messages", "settings"].map((k) => axios.get(`${API}/admin/${k}`, cfg).then((r) => r.data)),
    );
    setData({ stats, users, requests, payments, notifications, messages, settings });
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, cfg);
        setUser(me.data);
        if (!me.data.is_admin) {
          toast.error("Area riservata al team FArLight");
          navigate("/account");
          return;
        }
        await load();
        setReady(true);
      } catch {
        navigate("/login");
      }
    })();
  }, []);

  const markRead = async () => {
    await axios.post(`${API}/admin/notifications/read`, {}, cfg);
    load();
  };

  if (!ready) {
    return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-8 h-8 text-amberdark animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-cream">
      <header className="border-b border-ink/10 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-ink" aria-label="FArLight"><Logo className="h-9 w-auto" accent="#B8875A" /><span className="text-xs uppercase tracking-[0.2em] text-inksoft">Admin</span></Link>
          <Link to="/account" data-testid="admin-back-account" className="text-xs font-semibold uppercase tracking-[0.15em] text-inksoft hover:text-ink">Area personale</Link>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <h1 data-testid="admin-title" className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-ink">Dashboard di controllo</h1>
        <DemoModeToggle />
        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)} className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] rounded-sm px-4 h-10 transition-colors ${tab === key ? "bg-ink text-cream" : "border border-ink/15 text-inksoft hover:text-ink"}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} />{label}
              {key === "notifications" && data.stats?.unread_notifications > 0 && <span className="ml-1 bg-amberl text-espresso rounded-full text-[10px] px-1.5">{data.stats.unread_notifications}</span>}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "overview" && <Overview stats={data.stats} />}
          {tab === "users" && <UsersTab users={data.users} reload={load} />}
          {tab === "requests" && <RequestsTab requests={data.requests} onUpdate={(u) => setData((d) => ({ ...d, requests: d.requests.map((x) => (x.id === u.id ? { ...x, ...u, delivered_at: u.work_status === "delivered" ? x.delivered_at || new Date().toISOString() : x.delivered_at } : x)) }))} />}
          {tab === "payments" && <PaymentsTab payments={data.payments} />}
          {tab === "notifications" && <NotificationsTab notifications={data.notifications} markRead={markRead} />}
          {tab === "messages" && <MessagesTab messages={data.messages} />}
          {tab === "database" && <div className="space-y-8"><KnowledgeBasePanel /><TemplatesPanel /></div>}
          {tab === "portfolio" && <PortfolioPanel />}
          {tab === "settings" && <SettingsTab settings={data.settings} reload={load} />}
        </div>
      </main>
    </div>
  );
}
